import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Briefcase, ArrowRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { getJobs } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const TYPES = { full_time: "Full-time", contract: "Contract", intern: "Internship" };

export default function Careers() {
  const [items, setItems] = useState([]);
  const [dept, setDept] = useState("");
  const [type, setType] = useState("");

  useEffect(() => { getJobs({ active: true }).then(setItems).catch(() => {}); }, []);

  const depts = useMemo(() => Array.from(new Set(items.map((i) => i.department))), [items]);
  const types = useMemo(() => Array.from(new Set(items.map((i) => i.type))), [items]);

  const filtered = items.filter((it) => {
    if (dept && it.department !== dept) return false;
    if (type && it.type !== type) return false;
    return true;
  });

  useSEO({
    title: "Careers at RockLadder Technologies",
    description: "Work with operators who ship detections that matter. Open roles in MDR, consulting, cloud, and offensive security.",
    canonical: "https://rockladder.com/careers",
  });

  return (
    <>
      <PageHero
        eyebrow="CAREERS"
        title="Ship security"
        gradient="that actually stops things."
        subtitle="We hire operators — analysts, engineers, and consultants who publish, mentor, and take the pager."
        breadcrumb={[{ label: "Careers" }]}
      />
      <section className="relative py-12">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 mb-10">
            <span className="font-mono text-[10px] tracking-[0.22em] text-inkText-muted mr-2 self-center">DEPARTMENT</span>
            <button onClick={() => setDept("")} className={`font-mono text-[10px] tracking-[0.22em] px-3 py-2 rounded-full border transition-colors ${dept === "" ? "border-gold text-gold" : "border-gold/20 text-inkText-muted"}`}>ALL</button>
            {depts.map((d) => (
              <button key={d} onClick={() => setDept(d)} data-testid={`careers-filter-${d.toLowerCase()}`} className={`font-mono text-[10px] tracking-[0.22em] px-3 py-2 rounded-full border transition-colors ${dept === d ? "border-gold text-gold" : "border-gold/20 text-inkText-muted hover:border-gold/50"}`}>
                {d.toUpperCase()}
              </button>
            ))}
            <span className="font-mono text-[10px] tracking-[0.22em] text-inkText-muted ml-4 mr-2 self-center">TYPE</span>
            <button onClick={() => setType("")} className={`font-mono text-[10px] tracking-[0.22em] px-3 py-2 rounded-full border transition-colors ${type === "" ? "border-gold text-gold" : "border-gold/20 text-inkText-muted"}`}>ALL</button>
            {types.map((t) => (
              <button key={t} onClick={() => setType(t)} className={`font-mono text-[10px] tracking-[0.22em] px-3 py-2 rounded-full border transition-colors ${type === t ? "border-gold text-gold" : "border-gold/20 text-inkText-muted hover:border-gold/50"}`}>
                {TYPES[t]?.toUpperCase() || t.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {filtered.map((j) => (
              <Link
                key={j.slug}
                to={`/careers/${j.slug}`}
                data-testid={`job-${j.slug}`}
                className="group flex items-center justify-between gap-4 glass-panel rounded-2xl p-5 md:p-6 hover:border-gold transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-1.5">
                    <span className="font-mono text-[10px] tracking-[0.22em] text-gold-body">{j.department.toUpperCase()}</span>
                    <span className="font-mono text-[10px] tracking-[0.22em] text-inkText-muted">{TYPES[j.type] || j.type}</span>
                    <span className="font-mono text-[10px] tracking-[0.22em] text-inkText-muted">{j.experience}</span>
                  </div>
                  <div className="font-display text-lg md:text-xl text-inkText-primary group-hover:text-gold transition-colors">{j.title}</div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-inkText-muted">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {j.location}</span>
                    <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" /> {TYPES[j.type] || j.type}</span>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-inkText-muted group-hover:text-gold group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-inkText-muted text-sm">
              No matching roles. Send your resume to careers@rockladder.com anyway — we&apos;re always hiring exceptional operators.
            </div>
          )}
        </div>
      </section>
    </>
  );
}
